import React from 'react';

import { observer, useSelector } from '@datagrid/state';
import { useNavigate, useParams } from 'react-router-dom';

import { Box, Button, Icon, Select, Stack, TFText, Title } from '@tf/ui';

import { appStore } from '@/core/stores';
import { EditableLabel } from '@/components/internal-tools/EditableLabel';
import { ListItemCard } from '@/components/internal-tools/ListItemCard';
import { Menu } from '@/components/internal-tools/Menu';
import { paths } from '@/components/internal-tools/paths';
import { RefLink } from '@/components/internal-tools/RefLink';

type SelectOption = {
	value: string;
	label: string;
};

const OptionsView: React.FC<{
	enumName: string;
	options: SelectOption[];
	onUpdate: (newOptions: SelectOption[]) => void;
}> = observer(({ enumName, options, onUpdate }) => {
	const updateLabel = (optionToEdit: SelectOption, label: string) => {
		const newOptions = options.map((o) =>
			o.value === optionToEdit.value ? { ...optionToEdit, label } : o
		);
		onUpdate(newOptions);
	};

	const onDelete = (option: SelectOption) => {
		const newOptions = options.filter((o) => o.value !== option.value);
		onUpdate(newOptions);
	};

	// "Used by"
	const structs = useSelector(() => appStore.defs.get().structs);
	const usedBy: { fieldLabel: string; fieldName: string; structName: string }[] = [];
	Object.entries(structs).forEach(([name, struct]) => {
		struct.fields.forEach((f) => {
			if (f.kind === 'enum' && f.ref === enumName) {
				usedBy.push({ fieldLabel: f.visual.label, fieldName: f.name, structName: name });
			}
		});
	});

	return (
		<Stack gap="16px">
			<Title order={2}>{enumName}</Title>

			<Stack gap="8px">
				<Title order={3}>Preview</Title>
				<Select data={options} />
			</Stack>

			<Stack gap="8px">
				<Title order={3}>Used by</Title>
				{usedBy.length === 0 ? <div>Not used!</div> : null}
				<ul style={{ margin: 0, paddingLeft: '16px' }}>
					{usedBy.map((item) => (
						<li key={`${item.structName}.${item.fieldName}`}>
							{item.fieldLabel} [{item.fieldName}] in {item.structName}{' '}
							<RefLink to={paths.structPage(item.structName)} />
						</li>
					))}
				</ul>
			</Stack>

			<Stack gap="8px">
				<Title order={3}>Options</Title>
				<Stack gap="4px">
					{options.map((option) => (
						<ListItemCard key={option.label}>
							{/* Info */}
							<Box w="100%">
								<EditableLabel
									initialValue={option.label}
									onUpdate={(label) => updateLabel(option, label)}
								/>
								<TFText c="dimmed">{option.value}</TFText>
							</Box>

							{/* Actions */}
							{/* NOTE: removing editing from enums and structs viewer */}
							{/*<Box>*/}
							{/*	<Button size="xs" variant="subtle" color="red" onClick={() => onDelete(option)}>*/}
							{/*		<Icon.IconTrash size={14} />*/}
							{/*	</Button>*/}
							{/*</Box>*/}
						</ListItemCard>
					))}
				</Stack>
			</Stack>
		</Stack>
	);
});

export const EnumsRoute: React.FC = observer(() => {
	const enums = useSelector(() => appStore.defs.get().options);
	const enumNamesList = Object.keys(enums).filter((name) => {
		// Filter out "system" enums
		const startsWithCapital = /^[A-Z]/.test(name);
		return !startsWithCapital;
	});

	const params = useParams();
	const navigate = useNavigate();
	const selectedEnumName = params.enumName ?? '';
	const selectedEnum = enums[selectedEnumName] ?? null;

	const updateSelectedEnum = (options: SelectOption[]) => {
		appStore.defs.options.set((prev) => {
			return {
				...prev,
				[selectedEnumName]: options,
			};
		});
	};

	return (
		<Box sx={{ display: 'flex', gap: '16px' }}>
			<Menu
				items={enumNamesList}
				selectedItem={selectedEnumName}
				onSelect={(enumName) => navigate(paths.enumPage(enumName))}
			/>

			<Box w="100%">
				{!selectedEnum ? (
					<div>Select enum</div>
				) : (
					<OptionsView
						key={selectedEnumName}
						enumName={selectedEnumName}
						options={selectedEnum}
						onUpdate={updateSelectedEnum}
					/>
				)}
			</Box>
		</Box>
	);
});
